import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Divider, Headline, Layout, Text } from '@oneaudi/unified-web-alpha-components';
import { useEffect, useMemo, useState } from 'react';
import { CarCard } from '../car-card/car-card';
import { useI18n } from '../../i18n/i18n';
import { TagSelection } from '../tag-selection/tag-selection';
import { TechnicalDataSections } from './tech-data-sections';
import { useModelTechnicalDataSection } from '../../hooks/use-technical-data-sections';
import { useCarlineStore } from '../../store/carline';
import { useDebugMode } from '../../utils/debug-mode';
import { ShowDifferencesToggle } from '../showDifferencesToggle';
export const TechData = ({ actions }) => {
    var _a, _b;
    const { i18n } = useI18n();
    const { isDebug, getDebugCode } = useDebugMode();
    const [selectedTags, setSelectedTags] = useState([]);
    const [showDifferences, setShowDifferences] = useState(false);
    const { configuredCar, appConfig, selectedEngine, compare, secondSelectedEngine, setSelectedEngine, setSecondSelectedEngine, } = useCarlineStore();
    const leftTechDataSections = useModelTechnicalDataSection(selectedEngine === null || selectedEngine === void 0 ? void 0 : selectedEngine.technicalDataV2);
    const rightTechDataSections = useModelTechnicalDataSection(secondSelectedEngine === null || secondSelectedEngine === void 0 ? void 0 : secondSelectedEngine.technicalDataV2);
    const handleChangeInitialCar = (model) => {
        var _a, _b;
        setSelectedEngine(model);
        (_a = actions === null || actions === void 0 ? void 0 : actions.onChangeInitialStandardEquipmentCar) === null || _a === void 0 ? void 0 : _a.call(actions, model);
        (_b = actions === null || actions === void 0 ? void 0 : actions.onChangeInitialTechDataCar) === null || _b === void 0 ? void 0 : _b.call(actions, model);
    };
    const handleChangeCompareCar = (model) => {
        var _a, _b;
        setSecondSelectedEngine(model);
        (_a = actions === null || actions === void 0 ? void 0 : actions.onChangeCompareStandardEquipmentCar) === null || _a === void 0 ? void 0 : _a.call(actions, model);
        (_b = actions === null || actions === void 0 ? void 0 : actions.onChangeCompareTechDataCar) === null || _b === void 0 ? void 0 : _b.call(actions, model);
    };
    const handleSelectTag = (tag) => {
        const tagExists = selectedTags.includes(tag);
        if (tagExists) {
            setSelectedTags((prev) => prev.filter((t) => t !== tag));
        }
        else {
            setSelectedTags((prev) => [...prev, tag]);
        }
    };
    const handleResetFilters = () => {
        setSelectedTags([]);
    };
    const { filteredLeftTechDataSections, filteredRightTechDataSections, filteredTags } = useMemo(() => {
        let firstFiltered = leftTechDataSections;
        let secondFiltered = rightTechDataSections;
        let sectionsForTags = leftTechDataSections;
        if (showDifferences) {
            const secondMap = new Map(secondFiltered.map((section) => [section.key, section]));
            const firstDiff = [];
            const secondDiff = [];
            for (const firstSection of firstFiltered) {
                const secondSection = secondMap.get(firstSection.key);
                if (!secondSection)
                    continue;
                const secondEntryMap = new Map(secondSection.entries.map((e) => [e.key, e]));
                const differingFirstEntries = [];
                const differingSecondEntries = [];
                for (const entry of firstSection.entries) {
                    const matching = secondEntryMap.get(entry.key);
                    if (!matching || entry.value !== matching.value) {
                        differingFirstEntries.push(entry);
                        differingSecondEntries.push(matching !== null && matching !== void 0 ? matching : { title: entry.title, value: '-', key: entry.key });
                    }
                }
                if (differingFirstEntries.length > 0) {
                    firstDiff.push({
                        title: firstSection.title,
                        entries: differingFirstEntries,
                        key: firstSection.key,
                    });
                    secondDiff.push({
                        title: secondSection.title,
                        entries: differingSecondEntries,
                        key: secondSection.key,
                    });
                }
            }
            firstFiltered = firstDiff;
            secondFiltered = secondDiff;
            sectionsForTags = firstDiff;
        }
        const filterByTags = (sections) => selectedTags.length
            ? sections.filter(({ key }) => selectedTags.includes(key) || key === 'emissionData')
            : sections;
        return {
            filteredLeftTechDataSections: filterByTags(firstFiltered),
            filteredRightTechDataSections: filterByTags(secondFiltered),
            filteredTags: sectionsForTags.flatMap((section) => {
                return {
                    value: section.title,
                    label: section.title,
                    key: section.key,
                };
            }),
        };
    }, [leftTechDataSections, rightTechDataSections, selectedTags, showDifferences]);
    useEffect(() => {
        const validTagSet = new Set(filteredTags.map((t) => t.key));
        const filtered = selectedTags.filter((tag) => validTagSet.has(tag));
        if (filtered.length !== selectedTags.length) {
            setSelectedTags(filtered);
        }
    }, [filteredTags, selectedTags]);
    const nameToShow = (_b = (_a = configuredCar.carlineStructureCarline) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : 'Carline';
    return (_jsxs(Layout, { margin: ['40px', 0, 0, 0], direction: "column", rowGap: "40px", children: [compare && (_jsxs(Headline, { tag: "h1", variant: {
                    order: '2',
                    type: 'headline',
                    weight: 'normal',
                }, children: [nameToShow, isDebug && (_jsxs(_Fragment, { children: [_jsx("br", {}), _jsx(Text, { tag: "span", variant: { type: 'copy', order: '2', style: 'normal' }, children: ` [${selectedEngine ? getDebugCode(selectedEngine) : ''}]` })] }))] })), _jsxs(Layout, { direction: "row", columnGap: "18px", children: [_jsx(CarCard, { setSelectedEngine: (model) => {
                            handleChangeInitialCar(model);
                        }, selectedEngine: selectedEngine, isSelectedDriveTrain: appConfig === null || appConfig === void 0 ? void 0 : appConfig.isSelectedDriveTrain }), compare && (_jsx(CarCard, { setSelectedEngine: (model) => {
                            handleChangeCompareCar(model);
                        }, selectedEngine: secondSelectedEngine, isSelectedDriveTrain: appConfig === null || appConfig === void 0 ? void 0 : appConfig.isSelectedDriveTrainCompare, hasNoDefaultEngine: true }))] }), _jsx(Divider, { margin: ['0', '0', '8px', '0'] }), _jsx(Layout, { direction: "column", rowGap: "25px", children: _jsx(TagSelection, { title: i18n('compareLayer.header.tagSelection.title'), subTitle: i18n('compareLayer.header.tagSelection.subTitle'), tags: filteredTags, selectedTags: selectedTags, onSelect: handleSelectTag, onResetFilters: handleResetFilters }) }), _jsx(Divider, {}), compare && (_jsx(ShowDifferencesToggle, { showDifferences: showDifferences, setShowDifferences: setShowDifferences })), _jsx(TechnicalDataSections, { leftTechDataSections: filteredLeftTechDataSections, rightTechDataSections: filteredRightTechDataSections })] }));
};
